import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { DotCMSEditablePageService } from '@dotcms/angular';
import { DotCMSExtendedPageResponse, DotCMSPageRequestParams } from '@dotcms/types';
import { getUVEState } from '@dotcms/uve';
import { Store } from '@ngrx/store';
import { SeoService } from 'src/app/extensions/seo/services/seo/seo.service';

import { SeoSchemaService } from 'ish-core/services/seo-schema/seo-schema.service';
import { applyConfiguration } from 'ish-core/store/core/configuration';

import { DotCMSPageProps } from '../../models/dot-cms-uve-contentlet.model';
import { ComposedPageResponse, PageState } from '../../models/dot-cms-uve.model';
import { isDotCmsPage, navigateTo } from '../../utils/dot-cms-uve-utils';
import { DotCmsUvePageService, isTransientDotCmsError } from '../dot-cms-uve-page/dot-cms-uve-page.service';

@Injectable({ providedIn: 'root' })
export class DotCmsUveEditablePageService<T extends DotCMSExtendedPageResponse> {
  #pageService = inject(DotCmsUvePageService);
  #dotcmsEditablePageService = inject(DotCMSEditablePageService);
  #destroyRef = inject(DestroyRef);

  extraProps: DotCMSPageProps;

  readonly #context = signal<PageState<T>>({
    status: 'idle',
  });
  cleanUrl = '/';

  constructor(
    private store: Store,
    private seoSchemaeService: SeoSchemaService,
    private seoService: SeoService,
    private titleService: Title
  ) {}

  /**
   * Initializes the page by loading and managing page content based on the current route.
   * This method:
   * 1. Sets initial loading state
   * 2. Listens for route changes
   * 3. Fetches page content for the current URL
   * 4. Handles vanity URLs and redirects
   * 5. Manages UVE (Universal Visual Editor) integration
   * 6. Updates page state based on responses
   *
   * @param {DotCMSPageRequestParams} extraParams - Additional parameters to include in the page request
   * @returns {Signal<PageState<T>>} A signal containing the current page state
   */
  initializePage(extraParams: DotCMSPageRequestParams = {}, url?: string): Signal<PageState<T>> {
    this.#setLoading();

    if (!url) {
      console.error('No URL passed to initializePage!');
      return this.#context.asReadonly();
    }

    let cleanUrl = url;

    if (cleanUrl.includes('/home')) {
      cleanUrl = '/';
    }

    // every other route also requests the root page, for the header and footer it carries
    const isOwnPage = cleanUrl.startsWith('/page/') && isDotCmsPage(cleanUrl);

    if (isOwnPage) {
      cleanUrl = cleanUrl.replace('/page', '');
    } else {
      cleanUrl = '/';
    }

    const pageRequest$ = /\/blog\/post\//.test(cleanUrl)
      ? this.#pageService.getBlogDetailPageAsset(cleanUrl, extraParams)
      : this.#pageService.getPageAsset<T>(cleanUrl, extraParams);
    pageRequest$.subscribe(response => {
      const resp = response as ComposedPageResponse<T> | any;

      this.seoSchemaeService.setContentPageSchemaData(resp?.content?.extraPageProps?.schema);
      this.extraProps = resp?.content?.extraPageProps;

      this.setMetaData(isOwnPage ? resp?.pageAsset?.page?.title : undefined);
      const vanityUrl = resp.pageAsset?.vanityUrl;
      const action = vanityUrl?.action ?? 0;

      if (action > 200) {
        navigateTo(vanityUrl?.forwardTo);
        return;
      }

      // A FAILED REQUEST IS NO LONGER SILENT, AND INSIDE THE EDITOR IT IS NO LONGER FATAL.
      //
      // This used to be gated on `!getUVEState()`, so inside the editor a failure was skipped
      // entirely — nothing logged — and the error object fell through to `listen()`. That matters
      // because `listen()` passes whatever it is given straight to `initUVE`, and
      // `setClientIsReady` posts that object verbatim as the CLIENT_READY payload. dotCMS's
      // handler is `if (query) uveStore.setCustomClient({ query, variables })`, so an object with
      // no `graphql` meant our query was never installed and the canvas stayed empty.
      //
      // The recovery is that the EDITOR does not need our fetch to have succeeded. It re-runs the
      // query under the author's own session — where an unpublished page resolves and the
      // permissions are the author's — so as long as the response carries a query, handing it over
      // is strictly better than erroring. `getPageAsset` rebuilds one for exactly this case.
      if (resp?.error) {
        const insideUVE = !!getUVEState();
        const canRecoverInEditor = insideUVE && !!resp?.graphql?.query;

        if (canRecoverInEditor) {
          console.warn('[DotCmsUve] page request failed — handing the query to the editor to re-run', {
            url: cleanUrl,
            error: resp.error,
          });
        } else if (SSR && isTransientDotCmsError(resp.error)) {
          // CUSTOMIZATION: the server could not reach dotCMS (connection failure, not an answer). Setting the
          // error state here redirects to the "Page not found!" error page for a page that exists. The page is
          // left unset instead: the browser requests it again itself, with retries, and decides then.
          console.warn('[DotCmsUve] page request failed on the server, left to the browser', {
            url: cleanUrl,
            error: resp.error?.message,
          });

          return this.#context.asReadonly();
        } else {
          console.error('[DotCmsUve] page request failed', { url: cleanUrl, insideUVE, error: resp.error });

          this.#setError({
            message: resp?.error?.message,
            status: 'Error',
          });

          return this.#context.asReadonly();
        }
      }

      // If UVE is not enabled, set the page content
      if (!getUVEState()) {
        this.#setPageContent(resp as ComposedPageResponse<T>);

        return;
      }
      // If UVE is enabled, listen for changes
      let editorLang: string;
      this.#dotcmsEditablePageService
        .listen(resp)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(page => {
          // The editor sends the whole page again after every change (an Edit Styles choice, a moved contentlet).
          // The configuration only changes with the language: dispatching it each time made the store recompute every
          // selector, which froze the page for some hundred milliseconds right when an animation was to start.
          const lang = `${page?.pageAsset?.viewAs?.language?.languageCode}_${page?.pageAsset?.viewAs?.language?.countryCode}`;
          if (lang !== editorLang) {
            editorLang = lang;
            this.store.dispatch(applyConfiguration({ lang }));
          }
          // Set the page content every time it changes
          this.#setPageContent(page as ComposedPageResponse<T>);
        });
    });

    return this.#context.asReadonly();
  }

  /**
   * Set the page content
   * @param page
   */
  #setPageContent(page?: ComposedPageResponse<T>) {
    this.#context.set({
      pageResponse: page,
      status: 'success',
      error: undefined,
    });
  }

  /**
   * Set the loading state
   */
  #setLoading() {
    this.#context.update(state => ({
      ...state,
      status: 'loading',
      error: undefined,
    }));
  }

  /**
   * Set the error state
   * @param error
   */
  #setError(error: PageState<T>['error']) {
    this.#context.update(state => ({
      ...state,
      error,
      status: 'error',
    }));
  }

  /**
   * @param pageTitle the title in dotCMS of the page a `/page/...` route shows, used when that page has no SEO
   *                  title. Without it the tab kept the title of the page visited before, as nothing else sets
   *                  one for these routes. Not passed for the root page other routes request, which would
   *                  overwrite the titles of the shop pages.
   */
  setMetaData(pageTitle?: string) {
    if (this.extraProps) {
      this.applySeoMeta(this.extraProps);
    }
    const title = this.extraProps?.seoTitle?.trim() || pageTitle?.trim();
    if (title) {
      this.titleService.setTitle(title);
    }
  }

  applySeoMeta(extra: DotCMSPageProps) {
    if (extra?.metaDescription?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        name: 'description',
        content: extra.metaDescription,
      });
    }

    if (extra?.metaKeywords?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        name: 'keywords',
        content: extra.metaKeywords,
      });
    }

    if (extra?.canonicalUrl?.trim().length > 0) {
      this.seoService.setCanonicalUrl(extra.canonicalUrl);
    }

    if (extra?.searchEngines?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        name: 'robots',
        content: extra.searchEngines, // e.g. "index, follow"
      });
    }

    // Open Graph
    if (extra?.ogTitle?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        property: 'og:title',
        content: extra.ogTitle,
      });
    }

    if (extra?.ogDescription?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        property: 'og:description',
        content: extra.ogDescription,
      });
    }

    if (extra?.ogType?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        property: 'og:type',
        content: extra.ogType,
      });
    }

    if (extra?.ogImage?.trim().length > 0) {
      this.seoService.addOrModifyTag({
        property: 'og:image',
        content: extra.ogImage,
      });
    }
  }
}
